/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useRef} from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {
  MaterialExitCloseIcon
} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {
  TopNavSearchIcon
} from '@ion/dls/src/icons/TopNavSearchIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface FilterSearchProps {
  onChange: (value: string) => void;
  openFilterSearch?: boolean;
  setOpenFilterSearch?: (val: boolean) => void;
  fullWidth?: boolean;
  clearSearch?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  suppressAutoFocusOnOpen?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  searchBar : {
    position                       : 'relative',
    [theme.breakpoints.down('sm')] : {
      margin : '10px 0 20px'
    }
  },
  searchIcon : {
    position   : 'absolute',
    right      : '0px',
    opacity    : '1',
    transition : 'opacity 400ms ease-in'
  },
  filterSearch           : {},
  hiddenFilterSearchIcon : {
    opacity    : '0',
    transition : 'opacity 300ms ease-out'
  },
  closedFilterSearch : {
    width      : '0px',
    transition : 'width 300ms ease-out',
    '& button' : {
      width : '0px'
    }
  },
  openFilterSearch : {
    width                          : ({fullWidth}: {fullWidth?: boolean}) => fullWidth ? '100%' : '366px',
    transition                     : 'width 300ms ease-in',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  filterSearchInputUnderline : {
    '&:before' : {
      display : ({openFilterSearch}: {openFilterSearch: boolean}) =>
        !openFilterSearch && 'none'
    },
    '&:hover' : {
      '&:not(.Mui-disabled)' : {
        '&:before' : {
          borderBottom : ({openFilterSearch}: {openFilterSearch: boolean}) =>
            openFilterSearch ? `2px solid ${dlsColor('caBlue')}` : 'none'
        }
      }
    },
    '&:after' : {
      borderBottom : ({openFilterSearch}: {openFilterSearch: boolean}) =>
        openFilterSearch ? `2px solid ${dlsColor('caBlue')}` : 'none'
    },
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        padding    : '10px 0 !important',
        '&:before' : {
          display      : 'block',
          borderBottom : `2px solid ${dlsColor('grey5')}`
        },
        '&:hover' : {
          '&:not(.Mui-disabled)' : {
            '&::before' : {
              borderBottom : `2px solid ${dlsColor('caBlue')}`
            }
          }
        },
        '&:after' : {
          borderBottom : `2px solid ${dlsColor('caBlue')}`
        }
      }
    }
  },
  filterSearchAdornment : {
    cursor                         : 'pointer',
    position                       : 'relative',
    left                           : '5px',
    [theme.breakpoints.down('sm')] : {
      left : '-4px'
    }
  }
}));

/**
 * Search field for filters. Props allow it to be closed with search icon which
 * opens the search field when clicked on or can be persisted open.
 *
 * ```typescript
 * import {FilterSearch} from '@ion/dls/src/components/filters';
 *
 * <FilterSearch fullWidth
 *   onChange={onChange}
 *   openFilterSearch={openFilterSearch}
 *   setOpenFilterSearch={setOpenFilterSearch}
 *   fullWidth={fullWidth}
 *   clearSearch={clearSearch}
 *   disabled={disabled} />
 *
 * ```
 *
 */
export const FilterSearch = ({
  onChange,
  openFilterSearch,
  searchPlaceholder,
  setOpenFilterSearch,
  fullWidth = false,
  clearSearch = false,
  disabled = false,
  suppressAutoFocusOnOpen = false
}: FilterSearchProps) => {
  const filterSearchInputRef = useRef(null);
  const classes              = useStyles({openFilterSearch, fullWidth});
  const {isPhone}            = useMedia();

  useEffect(() => {
    if (openFilterSearch && !suppressAutoFocusOnOpen) {
      filterSearchInputRef.current.focus();
    }
  }, [openFilterSearch]);

  useEffect(() => {
    if (clearSearch && filterSearchInputRef.current) {
      filterSearchInputRef.current.value = '';
    }
  }, [clearSearch]);

  function toggleFilterSearch() {
    if (openFilterSearch) {
      filterSearchInputRef.current.value = '';
    }
    setOpenFilterSearch?.(!openFilterSearch);
  }

  function clearMobileValue() {
    filterSearchInputRef.current.value = '';
    filterSearchInputRef.current.focus();
    onChange('');
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const {target: {value}} = e;
    e.persist();
    onChange(value);
  }

  return (
    <div className={classes.searchBar}>
      <div className={classnames(
        classes.searchIcon,
        openFilterSearch || isPhone ? classes.hiddenFilterSearchIcon : null
      )}>
        <IconButton disableRipple
          style={{padding: '9px 9px 8px 9px'}}
          onClick={toggleFilterSearch}
          icon={<TopNavSearchIcon />} />
      </div>
      <div className={classnames(
        classes.filterSearch,
        openFilterSearch || isPhone ? classes.openFilterSearch : classes.closedFilterSearch
      )}>
        <TextField autoFocus={!suppressAutoFocusOnOpen}
          placeholder={translate(searchPlaceholder || 'common:search.searchPlaceholderShort')}
          onChange={handleChange}
          disabled={disabled}
          inputProps={{
            ref : filterSearchInputRef
          }}
          InputProps={{
            classes : {
              underline : classes.filterSearchInputUnderline
            },
            endAdornment : (
              <InputAdornment position='end'
                classes={{
                  root : classes.filterSearchAdornment
                }}>
                <IconButton disableRipple onClick={isPhone ? clearMobileValue : toggleFilterSearch}
                  icon={<MaterialExitCloseIcon />} size='small' />
              </InputAdornment>
            )
          }} />
      </div>
    </div>
  );
};

export default FilterSearch;
